import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { normalizeShopLink } from '@/lib/shopHome';

/**
 * A section link: internal paths go through the router, https links open in a
 * new tab, anything else renders as a plain block (never a dead <a>).
 */
export const SmartLink = ({ link, className = '', children, ...rest }) => {
  const target = normalizeShopLink(link);
  if (target?.to) {
    return (
      <Link to={target.to} className={className} draggable={false} {...rest}>
        {children}
      </Link>
    );
  }
  if (target?.href) {
    return (
      <a href={target.href} target="_blank" rel="noopener noreferrer" className={className} draggable={false} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <div className={className} {...rest}>
      {children}
    </div>
  );
};

/** "Sponsored" / "Sponsored · Nova" — ads are always labelled (ASCI / E-Commerce Rules 2020). */
export const SponsoredLabel = ({ name, tone = 'muted', className = '' }) => {
  const look =
    tone === 'overlay'
      ? 'bg-black/55 text-white backdrop-blur-sm'
      : tone === 'onDark'
        ? 'bg-white/15 text-white'
        : 'bg-surface-sunken text-ink-500';
  return (
    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${look} ${className}`}>
      Sponsored{name ? ` · ${name}` : ''}
    </span>
  );
};

/** Title row shared by every merchandised block. */
export const SectionHeader = ({ title, subtitle, viewAllLink, viewAllLabel = 'View all', sponsored, sponsorName, icon: Icon, tone = 'light', className = '' }) => {
  if (!title && !viewAllLink && !sponsored) return null;
  const onDark = tone === 'dark';
  return (
    <div className={`mb-3 flex items-end justify-between gap-3 sm:mb-4 ${className}`}>
      <div className="min-w-0">
        {title && (
          <h2 className={`flex items-center gap-2 text-[1.2rem] font-bold leading-tight tracking-tight sm:text-h2 ${onDark ? 'text-white' : 'text-ink-900'}`}>
            {Icon && <Icon className={`h-5 w-5 shrink-0 sm:h-6 sm:w-6 ${onDark ? 'text-amber-300' : 'text-brand-secondary'}`} aria-hidden="true" />}
            <span className="min-w-0">{title}</span>
          </h2>
        )}
        {(subtitle || sponsored) && (
          <p className={`mt-1 flex flex-wrap items-center gap-2 text-sm ${onDark ? 'text-white/80' : 'text-ink-500'}`}>
            {sponsored && <SponsoredLabel name={sponsorName} tone={onDark ? 'onDark' : 'muted'} />}
            {subtitle && <span>{subtitle}</span>}
          </p>
        )}
      </div>
      {viewAllLink && (
        <SmartLink
          link={viewAllLink}
          data-item-key="view_all"
          data-track-manual=""
          className={`lk-focus group inline-flex min-h-[36px] shrink-0 items-center gap-1 rounded-full px-3 text-sm font-semibold transition ${
            onDark ? 'bg-white text-ink-900 hover:bg-white/90' : 'text-brand-secondary hover:bg-brand-tint'
          }`}
        >
          {viewAllLabel}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </SmartLink>
      )}
    </div>
  );
};

/**
 * White "shelf" a block sits on: edge to edge on phones (Flipkart-style, the
 * grey page shows between shelves), a rounded card from `sm` up.
 */
export const Shelf = ({ children, className = '', innerClassName = '', style }) => (
  <div className={`lk-container px-0 sm:px-6 lg:px-8 ${className}`}>
    <div className={`bg-white px-4 py-5 sm:rounded-3xl sm:border sm:border-ink-200/70 sm:p-6 sm:shadow-card ${innerClassName}`} style={style}>
      {children}
    </div>
  </div>
);

/** Two muted tones per name, so monogram tiles differ but stay on-palette. */
const TONES = [
  ['#1e3a8a', '#3b82f6'],
  ['#9d174d', '#ec4899'],
  ['#065f46', '#10b981'],
  ['#7c2d12', '#f97316'],
  ['#4c1d95', '#8b5cf6'],
  ['#155e75', '#0891b2'],
  ['#0e7490', '#22d3ee'],
];
export const toneFor = (label = '') => {
  let h = 0;
  for (const ch of String(label)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TONES[h % TONES.length];
};

export const initials = (text = '') =>
  String(text)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
